/**
 * Transaction Details Page
 * Shows comprehensive information about a specific transaction
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft,
    Calendar,
    User,
    CreditCard,
    Hash,
    Clock,
    ExternalLink,
    DollarSign,
    ShieldCheck,
} from 'lucide-react';
import { realAPI } from '../api/realEndpoints';
import { formatCurrency } from '../utils/helpers';
import { getUserImageSrc } from '../utils/userImage';
import Card from '../components/Card';
import Button from '../components/Button';
import './ContentManagement.css';

interface TransactionDetail {
    id: number;
    userId: number;
    providerId: number;
    bookingId: number;
    amount: number;
    platformCharge: number;
    chargeId: string | null;
    paymentStatus: string;
    createdAt: string;
    userInfo: {
        id: number;
        name: string;
        image: string | null;
        email: string;
        signInProvider?: string[] | null;
    };
    providerInfo: {
        id: number;
        name: string;
        image: string | null;
        email: string;
        signInProvider?: string[] | null;
    };
}

export default function TransactionDetailsPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [transaction, setTransaction] = useState<TransactionDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchTransactionDetails = async () => {
            if (!id) return;

            setIsLoading(true);
            try {
                const data = await realAPI.transactions.getDetails(id);
                setTransaction(data);
            } catch (err) {
                console.error('Failed to fetch transaction details:', err);
                setError('Failed to load transaction details');
            } finally {
                setIsLoading(false);
            }
        };

        fetchTransactionDetails();
    }, [id]);

    const getStatusColor = (status: string) => {
        const normalized = String(status).toLowerCase();
        switch (normalized) {
            case 'succeeded':
                return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
            case 'failed':
                return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
            case 'pending':
                return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
            case 'processing':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
            case 'refunded':
                return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
            case 'cancelled':
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="loader"></div>
            </div>
        );
    }

    if (error || !transaction) {
        return (
            <div className="space-y-4">
                <div className="mb-6">
                    <button
                        onClick={() => navigate('/transactions')}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors dark:text-gray-400 dark:hover:text-gray-100"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Transactions
                    </button>
                </div>
                <Card>
                    <div className="text-center py-12">
                        <p className="text-red-600">{error || 'Transaction not found'}</p>
                    </div>
                </Card>
            </div>
        );
    }

    const customerImageSrc = getUserImageSrc(
        transaction.userInfo?.image,
        transaction.userInfo?.signInProvider
    );
    const providerImageSrc = getUserImageSrc(
        transaction.providerInfo?.image,
        transaction.providerInfo?.signInProvider
    );

    return (
        <div className="dark:bg-gray-900">
            {/* Back Button */}
            <div className="mb-6">
                <button
                    onClick={() => navigate('/transactions')}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors dark:text-gray-400 dark:hover:text-gray-100"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Transactions
                </button>
            </div>

            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-1 h-8 bg-primary-500 rounded-full"></div>
                        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                            Transaction Details
                        </h1>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Financial Info */}
                    <div className="lg:col-span-2 space-y-6">
                        <Card className="overflow-hidden">
                            <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-6 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex justify-between items-start w-full">
                                    <div>
                                        <p className="text-sm text-gray-500 uppercase tracking-wider font-semibold">
                                            Total Amount
                                        </p>
                                        <div className="flex items-center gap-4 mt-1">
                                            <p className="text-4xl font-bold text-gray-900 dark:text-white">
                                                {formatCurrency(transaction.amount)}
                                            </p>

                                            <span
                                                className={`px-4 py-1 text-xs font-bold uppercase rounded-none ${getStatusColor(
                                                    transaction.paymentStatus
                                                )}`} style={{ borderRadius: '5px' }}
                                            >
                                                {transaction.paymentStatus}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="bg-white dark:bg-gray-800 p-3 rounded-xl shadow-sm">
                                        <CreditCard className="w-8 h-8 text-primary-500" />
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    <div className="flex items-start gap-3">
                                        <div className="mt-1">
                                            <ShieldCheck className="w-5 h-5 text-green-500" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-500">Platform Charge</p>
                                            <p className="text-lg font-medium text-gray-900 dark:text-white">
                                                {formatCurrency(transaction.platformCharge)}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <div className="mt-1">
                                            <Hash className="w-5 h-5 text-blue-500" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-500">Transaction ID</p>
                                            <p className="text-lg font-medium text-gray-900 dark:text-white font-mono">
                                                {transaction.id}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-start gap-3">
                                        <div className="mt-1">
                                            <Clock className="w-5 h-5 text-purple-500" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-500">Created At</p>
                                            <p className="text-lg font-medium text-gray-900 dark:text-white">
                                                {formatDate(transaction.createdAt)}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3">
                                        <div className="mt-1">
                                            <ExternalLink className="w-5 h-5 text-amber-500" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-gray-500">Charge ID</p>
                                            <p className="text-lg font-medium text-gray-900 dark:text-white break-all">
                                                {transaction.chargeId || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Related Booking */}
                        <Card className="hover:border-primary-500 transition-colors cursor-pointer group" onClick={() => navigate(`/bookings/${transaction.bookingId}`)}>
                            <div className="p-6 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 group-hover:bg-primary-500 group-hover:text-white transition-colors">
                                        <Calendar className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Related Booking</h3>
                                        <p className="text-gray-500">Booking ID: <span className="font-mono">{transaction.bookingId}</span></p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-primary-600 font-medium group-hover:translate-x-1 transition-transform">
                                    View Booking
                                    <ArrowLeft className="w-4 h-4 rotate-180" />
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Parties Sidebar */}
                    <div className="space-y-6">
                        {/* Customer Info */}
                        <Card className="overflow-hidden">
                            <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                                <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    <User className="w-4 h-4 text-blue-500" />
                                    Customer (Sender)
                                </h3>
                            </div>
                            <div className="p-6">
                                <div className="flex items-center gap-4 mb-6">
                                    {customerImageSrc ? (
                                        <img
                                            src={customerImageSrc}
                                            className="w-16 h-16 rounded-2xl object-cover shadow-sm"
                                            alt={transaction.userInfo.name}
                                        />
                                    ) : (
                                        <div className="w-16 h-16 rounded-2xl bg-blue-500 flex items-center justify-center text-white text-2xl font-bold shadow-sm">
                                            {transaction.userInfo?.name?.charAt(0)?.toUpperCase()}
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="text-lg font-bold text-gray-900 dark:text-white break-words">
                                            {transaction.userInfo?.name}
                                        </p>
                                        <p className="text-sm text-gray-500 break-all whitespace-normal">
                                            {transaction.userInfo?.email}
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    variant="secondary"
                                    className="w-full justify-center gap-2"
                                    onClick={() => navigate(`/customers/${transaction.userInfo?.id}`)}
                                >
                                    View Profile
                                </Button>
                            </div>
                        </Card>

                        {/* Provider Info */}
                        <Card className="overflow-hidden">
                            <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                                <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                    <User className="w-4 h-4 text-purple-500" />
                                    Provider (Receiver)
                                </h3>
                            </div>
                            <div className="p-6">
                                <div className="flex items-center gap-4 mb-6">
                                    {providerImageSrc ? (
                                        <img
                                            src={providerImageSrc}
                                            className="w-16 h-16 rounded-2xl object-cover shadow-sm"
                                            alt={transaction.providerInfo.name}
                                        />
                                    ) : (
                                        <div className="w-16 h-16 rounded-2xl bg-purple-500 flex items-center justify-center text-white text-2xl font-bold shadow-sm">
                                            {transaction.providerInfo?.name?.charAt(0)?.toUpperCase()}
                                        </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="text-lg font-bold text-gray-900 dark:text-white break-words">
                                            {transaction.providerInfo?.name}
                                        </p>
                                        <p className="text-sm text-gray-500 break-all whitespace-normal">
                                            {transaction.providerInfo?.email}
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    variant="secondary"
                                    className="w-full justify-center gap-2"
                                    onClick={() => navigate(`/providers/${transaction.providerInfo?.id}`)}
                                >
                                    View Profile
                                </Button>
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}
